import { useCallback, useEffect, useRef, useState } from "react";
import type { AntiCheatSignals } from "@/types/simulation";
import { ANTICHEAT_ENABLED } from "@/lib/config";

interface UseAntiCheat {
  /** True briefly after a paste attempt — drive the inline red warning + border flash. */
  pasteWarning: boolean;
  /** Handlers to spread onto the textarea (Layer 1 hard blocks). */
  textareaHandlers: {
    onPaste: (e: React.ClipboardEvent) => void;
    onCopy: (e: React.ClipboardEvent) => void;
    onCut: (e: React.ClipboardEvent) => void;
    onDrop: (e: React.DragEvent) => void;
    onContextMenu: (e: React.MouseEvent) => void;
    onKeyDown: (e: React.KeyboardEvent) => void;
  };
  /** Call on every textarea value change to feed the typing-rhythm detector. */
  noteChange: (length: number) => void;
  /** Snapshot the signals to send with the response. */
  collect: () => AntiCheatSignals;
  /** Reset all counters (call when moving to a new node). */
  reset: () => void;
}

/**
 * Anti-cheat Layers 1 + 2 for the Decision OpenText textarea.
 *  Layer 1 (hard blocks): paste / copy / cut / drop / context-menu prevented.
 *  Layer 2 (signals, logged not enforced): tab-blur count, typing-burst count
 *  (50+ chars within 500ms = faster than human typing), time-on-screen,
 *  keystroke count, paste-attempted flag.
 * Uses only standard browser events — nothing that trips corporate AV/CSP.
 *
 * Enablement: blocking is active iff the global ANTICHEAT_ENABLED env-var
 * AND the per-sim EnableAntiCheat setting are both ON. Either being OFF
 * disables blocking. Layer 2 signal collection runs regardless — it's
 * cheap and useful even when blocking is off.
 *
 * @param perSimEnabled  EnableAntiCheat setting for the current sim. Pass
 *                       undefined when the caller doesn't have the sim
 *                       tree yet (treated as ON to fail safe).
 */
export function useAntiCheat(perSimEnabled?: boolean): UseAntiCheat {
  const blockingActive = ANTICHEAT_ENABLED && (perSimEnabled ?? true);
  const [pasteWarning, setPasteWarning] = useState(false);

  const startedAt = useRef<number>(Date.now());
  const answerPasted = useRef(false);
  const tabBlurCount = useRef(0);
  const typingBurstCount = useRef(0);
  const keystrokeCount = useRef(0);
  const lastChange = useRef<{ t: number; len: number }>({ t: Date.now(), len: 0 });
  const warningTimer = useRef<number | null>(null);

  useEffect(() => {
    const onVisibility = (): void => {
      if (document.visibilityState === "hidden") tabBlurCount.current += 1;
    };
    const onBlur = (): void => {
      tabBlurCount.current += 1;
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("blur", onBlur);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("blur", onBlur);
      if (warningTimer.current !== null) window.clearTimeout(warningTimer.current);
    };
  }, []);

  const flashWarning = useCallback((): void => {
    setPasteWarning(true);
    if (warningTimer.current !== null) window.clearTimeout(warningTimer.current);
    warningTimer.current = window.setTimeout(() => setPasteWarning(false), 2500);
  }, []);

  const textareaHandlers = {
    onPaste: (e: React.ClipboardEvent): void => {
      answerPasted.current = true; // still record that a paste happened
      if (!blockingActive) return;
      e.preventDefault();
      flashWarning();
    },
    onCopy: (e: React.ClipboardEvent): void => {
      if (blockingActive) e.preventDefault();
    },
    onCut: (e: React.ClipboardEvent): void => {
      if (blockingActive) e.preventDefault();
    },
    onDrop: (e: React.DragEvent): void => {
      answerPasted.current = true;
      if (!blockingActive) return;
      e.preventDefault();
      flashWarning();
    },
    onContextMenu: (e: React.MouseEvent): void => {
      if (blockingActive) e.preventDefault();
    },
    onKeyDown: (e: React.KeyboardEvent): void => {
      // Count genuine character keystrokes (ignore modifiers/navigation)
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        keystrokeCount.current += 1;
      }
    },
  };

  const noteChange = useCallback((length: number): void => {
    const now = Date.now();
    const delta = length - lastChange.current.len;
    const elapsed = now - lastChange.current.t;
    // 50+ chars appearing within 500ms = faster than human typing → burst
    if (delta >= 50 && elapsed <= 500) {
      typingBurstCount.current += 1;
    }
    lastChange.current = { t: now, len: length };
  }, []);

  const collect = useCallback(
    (): AntiCheatSignals => ({
      answerPasted: answerPasted.current,
      tabBlurCount: tabBlurCount.current,
      typingBurstCount: typingBurstCount.current,
      decisionSecondsSpent: Math.round((Date.now() - startedAt.current) / 1000),
      keystrokeCount: keystrokeCount.current,
    }),
    [],
  );

  const reset = useCallback((): void => {
    startedAt.current = Date.now();
    answerPasted.current = false;
    tabBlurCount.current = 0;
    typingBurstCount.current = 0;
    keystrokeCount.current = 0;
    lastChange.current = { t: Date.now(), len: 0 };
    setPasteWarning(false);
  }, []);

  return { pasteWarning, textareaHandlers, noteChange, collect, reset };
}
